/**
 * Счёт в поле суммы: «89,90×2+45».
 *
 * Нужен потому, что человек у кассы не знает итога — он знает слагаемые. Без
 * счёта в поле он считает в голове или в другом приложении, и ошибка на этом
 * шаге попадает в деньги молча.
 *
 * Вычисление целочисленное, в копейках. Ни одного промежуточного значения с
 * плавающей точкой: «0,1 + 0,2» в двоичной дроби даёт не 0,3, и такая ошибка
 * в деньгах накапливается.
 *
 * Второй операнд умножения и деления — множитель, а не деньги: «250×3» это три
 * раза по 250, а не произведение двух сумм. Поэтому он берётся как обычное
 * число, равное своему значению в рублях.
 */
import { ZERO, kop, roundHalfEven, type Money } from './money.js';

export type CalcError = 'empty' | 'syntax' | 'divzero' | 'overflow';

export interface CalcResult {
  /** Копейки либо null, если считать нечего или выражение неверно. */
  value: Money | null;
  error: CalcError | null;
}

/** Знаки, которые приходят с нашей клавиатуры и из системной. */
const normalizeOp = (c: string): string | null => {
  if (c === '+') return '+';
  if (c === '-' || c === '−' || c === '–' || c === '—') return '-';
  if (c === '*' || c === '×' || c === 'x' || c === 'х') return '*';
  if (c === '/' || c === '÷' || c === ':') return '/';
  return null;
};

/** Больше этого в бытовой сумме не бывает: дальше только ошибка ввода. */
const MAX_KOP = 1_000_000_000_00;

type Token = { n: Money } | { op: string };

export function evalAmount(expr: string): CalcResult {
  const tokens = tokenize(expr);
  // Разбор различает негодный знак и негодный размер: «сто рублей» и
  // «99999999999» ломаются по разным причинам, и называть их одинаково значит
  // потом гадать, что показать человеку.
  if (typeof tokens === 'string') return { value: null, error: tokens };
  if (tokens.length === 0) return { value: null, error: 'empty' };

  // Незакрытый хвост вида «1200+» — не ошибка, а середина набора: считаем то,
  // что уже введено, иначе итог мигал бы при каждом знаке.
  const last = tokens[tokens.length - 1];
  const body = last && 'op' in last ? tokens.slice(0, -1) : tokens;
  if (body.length === 0) return { value: null, error: 'empty' };

  // Первым должно стоять число: выражение не начинается со знака.
  const head = body[0];
  if (!head || 'op' in head) return { value: null, error: 'syntax' };

  // Умножение и деление раньше сложения и вычитания.
  const reduced: Token[] = [head];
  for (let i = 1; i < body.length; i += 2) {
    const opTok = body[i];
    const numTok = body[i + 1];
    if (!opTok || !('op' in opTok) || !numTok || !('n' in numTok)) {
      return { value: null, error: 'syntax' };
    }
    const prev = reduced[reduced.length - 1];
    if (opTok.op === '*' || opTok.op === '/') {
      if (!prev || !('n' in prev)) return { value: null, error: 'syntax' };
      const r = opTok.op === '*' ? mul(prev.n, numTok.n) : div(prev.n, numTok.n);
      if (r === null) return { value: null, error: opTok.op === '/' ? 'divzero' : 'overflow' };
      reduced[reduced.length - 1] = { n: r };
    } else {
      reduced.push({ op: opTok.op }, { n: numTok.n });
    }
  }

  let acc = (reduced[0] as { n: Money }).n;
  for (let i = 1; i < reduced.length; i += 2) {
    const opTok = reduced[i] as { op: string };
    const numTok = reduced[i + 1] as { n: Money } | undefined;
    if (!numTok) return { value: null, error: 'syntax' };
    acc = (opTok.op === '+' ? acc + numTok.n : acc - numTok.n) as Money;
  }

  if (!Number.isSafeInteger(acc) || Math.abs(acc) > MAX_KOP) {
    return { value: null, error: 'overflow' };
  }
  return { value: acc, error: null };
}

/**
 * Разбор строки в числа и знаки.
 *
 * Возвращает null на том, что числом не является. Пустая строка — пустой
 * список, а не ошибка: человек ещё ничего не набрал.
 */
function tokenize(expr: string): Token[] | 'syntax' | 'overflow' {
  const out: Token[] = [];
  let num = '';
  let fail: 'syntax' | 'overflow' | null = null;
  const pushNum = (): boolean => {
    if (num === '') return true;
    const v = parseNumber(num);
    if (typeof v === 'string') { fail = v; return false; }
    out.push({ n: v });
    num = '';
    return true;
  };

  for (const ch of expr) {
    if (/\d/.test(ch)) { num += ch; continue; }
    if (ch === ',' || ch === '.') { num += '.'; continue; }
    // Пробелы — разделители разрядов из поля ввода, они ничего не значат.
    if (/\s/.test(ch) || ch === ' ') continue;
    const op = normalizeOp(ch);
    if (op === null) return 'syntax';
    if (!pushNum()) return fail ?? 'syntax';
    const prev = out[out.length - 1];
    if (prev && 'op' in prev) {
      // Два знака подряд: последний заменяет предыдущий — так ведут себя
      // все калькуляторы, и человек не обязан стирать опечатку.
      out[out.length - 1] = { op };
    } else if (out.length === 0) {
      // Знак в начале: считать нечего.
      return 'syntax';
    } else {
      out.push({ op });
    }
  }
  if (!pushNum()) return fail ?? 'syntax';
  return out;
}

function parseNumber(raw: string): Money | 'syntax' | 'overflow' {
  if (!/^\d*\.?\d*$/.test(raw) || raw === '' || raw === '.') return 'syntax';
  const [int = '', frac = ''] = raw.split('.');
  // Больше двух знаков после запятой в деньгах не бывает: лишние отбрасываем,
  // а не округляем молча — округление здесь скрыло бы ошибку ввода.
  const cents = (frac + '00').slice(0, 2);
  const n = Number(`${int || '0'}.${cents}`);
  if (!Number.isFinite(n)) return 'syntax';
  const k = roundHalfEven(n * 100);
  // Само число разобралось, но в деньгах столько не бывает: это не опечатка в
  // знаках, а выход за диапазон, и называть их одинаково нельзя.
  if (!Number.isSafeInteger(k) || k > MAX_KOP) return 'overflow';
  return kop(k);
}

/** Умножение суммы на множитель: b берётся как число в рублях. */
function mul(a: Money, b: Money): Money | null {
  const k = roundHalfEven((a * b) / 100);
  if (!Number.isSafeInteger(k) || Math.abs(k) > MAX_KOP) return null;
  return kop(k);
}

/** Деление суммы на делитель: b берётся как число в рублях. */
function div(a: Money, b: Money): Money | null {
  if (b === ZERO) return null;
  const k = roundHalfEven((a * 100) / b);
  if (!Number.isSafeInteger(k) || Math.abs(k) > MAX_KOP) return null;
  return kop(k);
}
